import { cn } from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { useDictation } from '../../hooks/useDictation'
import { DictationControls } from '../dictation/DictationControls'
import type { DictationReviewSnapshot, DictationSessionToken, DictationTone } from '../dictation/dictation.types'
import LexicalMarkdownEditor, { MarkdownPreview } from '../MarkdownEditor'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ComponentProps, TextAreaCustomProps } from './types'

type FormsTextAreaProps = ComponentProps & {
	value?: string
	onChange?: (value: string) => void
	readOnly?: boolean
	hasError?: boolean
	dictationEnabled?: boolean
	allowAiRewrite?: boolean
	onRequestDictationToken?: () => Promise<DictationSessionToken | null>
	onRewriteDictatedText?: (input: string, tone: DictationTone) => Promise<string>
	onDictationSnapshot?: (snapshot: DictationReviewSnapshot) => void
}

export function FormsTextArea({
	component,
	value,
	onChange,
	readOnly,
	required,
	hasError,
	dictationEnabled = false,
	allowAiRewrite = false,
	onRequestDictationToken,
	onRewriteDictatedText,
	onDictationSnapshot,
}: FormsTextAreaProps) {
	const { label, labelColor, labelSize, labelWeight, placeholder, rows, textLimit, tooltipEnabled, tooltipText } =
		component.custom as TextAreaCustomProps
	const [internalValue, setInternalValue] = useState('')
	const [editorKey, setEditorKey] = useState(0)
	const lastEditorEmissionRef = useRef<string | undefined>(undefined)

	const handleChange = (val: string) => {
		lastEditorEmissionRef.current = val
		if (onChange) {
			onChange(val)
		} else {
			setInternalValue(val)
		}
	}
	const resolvedValue = value !== undefined ? value : internalValue
	const dictation = useDictation({
		fieldId: component.id,
		value: resolvedValue,
		profile: 'bullets',
		onRequestSessionToken: onRequestDictationToken,
		onSnapshot: onDictationSnapshot,
		onRewrite: onRewriteDictatedText,
		onChangeValue: handleChange,
	})

	useEffect(() => {
		if (readOnly || value === undefined) {
			return
		}

		// Only remount editor when value changes from an external source
		// (for example, applying an AI suggestion), not while typing.
		if (lastEditorEmissionRef.current !== value) {
			setEditorKey((prev) => prev + 1)
		}
	}, [value, readOnly])

	const maxWords = textLimit?.isWordLimit ? textLimit.words : undefined
	const maxCharacters = textLimit?.isWordLimit ? undefined : textLimit?.characters

	return (
		<div className='flex flex-col gap-1 w-full'>
			{label && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}
			{readOnly ? (
				<div className='bg-neutral-50 border border-neutral-200 rounded-md px-6 py-4'>
					<MarkdownPreview markdown={value || ''} />
				</div>
			) : (
				<div className={cn('flex flex-col items-end gap-1', hasError && 'border-[2px] border-error-600 rounded-md')}>
					<LexicalMarkdownEditor
						key={editorKey}
						onChangeMarkdown={handleChange}
						defaultValue={resolvedValue}
						reloadOnContentChange={true}
						rows={rows}
						placeholder={placeholder}
						maxWords={maxWords}
						maxCharacters={maxCharacters}
					/>
					{dictationEnabled && (
						<DictationControls
							allowRewrite={allowAiRewrite}
							isListening={dictation.isListening}
							isProcessing={dictation.state === 'processing'}
							error={dictation.error}
							interimTranscript={dictation.interimTranscript}
							draftTranscript={dictation.draftTranscript}
							onStart={() => void dictation.startDictation()}
							onStop={dictation.stopDictation}
							onInsert={dictation.insertDraft}
							onReplace={dictation.replaceWithDraft}
							canUndoRewrite={dictation.canUndoRewrite}
							onDiscard={dictation.discardDraft}
							onReset={dictation.resetDraft}
							onRewrite={(tone) => void dictation.rewriteText(tone)}
							onUndoRewrite={dictation.undoRewrite}
						/>
					)}
				</div>
			)}
		</div>
	)
}
